import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Send, Lock, Unlock, Maximize2, X, ImagePlus, Trash2, Download } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { key: "pending", color: "#FFB000" },
  { key: "in_progress", color: "#3FA7FF" },
  { key: "completed", color: "#00FF66" },
];

const MAX_RESPONSE_IMAGES = 5;
const MAX_IMG_BYTES = 2 * 1024 * 1024;
const ALLOWED_MIMES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

export default function AdminReviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseDraft, setResponseDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [enlarged, setEnlarged] = useState(null);
  const [ticketDraft, setTicketDraft] = useState("");
  const [ticketSending, setTicketSending] = useState(false);
  const ticketEndRef = useRef(null);

  // Pending images to attach with the next save (each: {base64, mime, filename, preview, sizeKB})
  const [pendingImages, setPendingImages] = useState([]);
  const imageInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/admin/reviews/${id}`);
      setReview(data);
      setResponseDraft(data.response || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (ticketEndRef.current) ticketEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [review?.ticket?.messages?.length]);

  const openImage = async () => {
    try {
      const { data } = await http.get(`/admin/reviews/${id}/image`);
      setEnlarged({
        src: `data:${data.image_mime};base64,${data.image_base64}`,
        filename: `chartlenz-user-chart-${id.slice(0, 8)}.${(data.image_mime || "image/png").split("/")[1]}`,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveResponse = async (statusKey = "completed") => {
    const text = responseDraft.trim();
    if (!text) { toast.error("Response cannot be empty"); return; }
    setSaving(true);
    try {
      await http.post(`/admin/reviews/${id}/respond`, {
        response: text,
        status: statusKey,
        images: pendingImages.map((p) => ({
          image_base64: p.base64, image_mime: p.mime, filename: p.filename,
        })),
      });
      if (statusKey === "completed") {
        toast.success("Review completed — back to console");
        navigate("/admin");
        return;
      }
      toast.success("Saved as draft");
      setPendingImages([]);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const addImages = (files) => {
    const arr = Array.from(files || []);
    const existingCount = (review?.response_images || []).length + pendingImages.length;
    const available = MAX_RESPONSE_IMAGES - existingCount;
    if (available <= 0) { toast.error(`Max ${MAX_RESPONSE_IMAGES} images per review`); return; }
    const next = [];
    for (const file of arr.slice(0, available)) {
      if (!ALLOWED_MIMES.includes(file.type)) { toast.error(`${file.name}: must be PNG/JPG/WEBP`); continue; }
      if (file.size > MAX_IMG_BYTES) { toast.error(`${file.name}: > 2 MB`); continue; }
      next.push(file);
    }
    next.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result;
        setPendingImages((prev) => [...prev, {
          base64: result.split(",")[1],
          mime: file.type,
          filename: file.name,
          preview: result,
          sizeKB: Math.round(file.size / 1024),
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePending = (idx) => {
    setPendingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const openResponseImage = async (img) => {
    try {
      const { data } = await http.get(`/admin/reviews/${id}/response-images/${img.id}`);
      setEnlarged({
        src: `data:${data.image_mime};base64,${data.image_base64}`,
        filename: data.filename || `chartlenz-${id.slice(0,8)}-${img.id.slice(0,6)}.png`,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteResponseImage = async (imgId) => {
    if (!window.confirm("Delete this screenshot? This cannot be undone.")) return;
    try {
      await http.delete(`/admin/reviews/${id}/response-images/${imgId}`);
      toast.success("Screenshot deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeStatus = async (status) => {
    try {
      await http.patch(`/admin/reviews/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendTicketReply = async () => {
    const msg = ticketDraft.trim();
    if (!msg || !review?.ticket) return;
    setTicketSending(true);
    try {
      await http.post(`/admin/tickets/${review.ticket.id}/reply`, { message: msg });
      setTicketDraft("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTicketSending(false); }
  };

  const setTicketStatus = async (status) => {
    if (!review?.ticket) return;
    try {
      await http.patch(`/admin/tickets/${review.ticket.id}/status`, { status });
      toast.success(`Ticket → ${status}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) {
    return <div className="pt-20 text-center text-xs font-mono uppercase tracking-[0.3em] text-neutral-600">loading…</div>;
  }
  if (!review) {
    return <div className="pt-20 text-center text-sm text-neutral-400">Review not found</div>;
  }

  return (
    <section className="pt-8 pb-20" data-testid="admin-review-detail">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <button
          onClick={() => navigate("/admin")}
          data-testid="back-to-admin"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.25em] text-neutral-400 hover:text-white mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> back to console
        </button>

        <div className="border border-neutral-900 bg-neutral-950 p-6">
          {/* Header row */}
          <div className="flex flex-wrap items-start justify-between gap-3 pb-4 border-b border-neutral-900">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFB000] mb-1.5">
                expert review · {review.id.slice(0, 8)}
              </div>
              <h1 className="text-3xl font-black tracking-tighter text-white">{review.asset}</h1>
              <div className="text-xs text-neutral-400 mt-1">
                {review.user_name || review.user_email || "—"}
                {review.user_email && review.user_name && (
                  <span className="text-neutral-600"> · {review.user_email}</span>
                )}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600 mt-1">
                submitted {new Date(review.created_at).toLocaleString()}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 items-end">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500">status</div>
              <div className="flex gap-1.5" data-testid="status-picker">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => changeStatus(s.key)}
                    data-testid={`status-${s.key}`}
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1.5 border ${
                      review.status === s.key
                        ? "text-white"
                        : "border-neutral-800 text-neutral-500 hover:border-neutral-600 hover:text-white"
                    }`}
                    style={review.status === s.key ? { color: s.color, borderColor: s.color } : {}}
                  >{s.key.replace("_", " ")}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Question + image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-2">user question</div>
              <div className="text-sm text-neutral-200 whitespace-pre-line leading-relaxed bg-black/40 border border-neutral-900 px-3 py-2.5">
                {review.question || <span className="text-neutral-600 italic">No question provided.</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-2">chart screenshot</div>
              {review.has_image ? (
                <button
                  onClick={openImage}
                  data-testid="view-chart-btn"
                  className="w-full border border-neutral-900 bg-black/40 hover:border-[#00E5FF] hover:text-[#00E5FF] text-neutral-300 px-3 py-2.5 flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-[0.2em] transition-colors"
                >
                  <Maximize2 className="w-3.5 h-3.5" /> view chart full-size
                </button>
              ) : (
                <div className="border border-neutral-900 bg-black/40 px-3 py-2.5 text-xs text-neutral-600 italic">
                  No screenshot provided.
                </div>
              )}
            </div>
          </div>

          {/* Response composer */}
          <div className="mt-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-2">your review response</div>
            <textarea
              value={responseDraft}
              onChange={(e) => setResponseDraft(e.target.value)}
              rows={10}
              placeholder="Write the full analysis here — market structure, key levels, trade ideas, risk…"
              data-testid="response-input"
              className="w-full bg-black border border-neutral-800 px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-[#00E5FF] resize-y leading-relaxed"
            />

            {/* Currently attached response images */}
            {(review.response_images && review.response_images.length > 0) && (
              <div className="mt-3" data-testid="response-images-existing">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-2">
                  attached screenshots ({review.response_images.length}/{MAX_RESPONSE_IMAGES})
                </div>
                <div className="flex flex-wrap gap-2">
                  {review.response_images.map((img) => (
                    <ResponseImageThumb
                      key={img.id}
                      onClick={() => openResponseImage(img)}
                      onDelete={() => deleteResponseImage(img.id)}
                      label={img.filename || img.id.slice(0, 6)}
                      testid={`response-image-${img.id}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Pending images (not yet saved) */}
            {pendingImages.length > 0 && (
              <div className="mt-3" data-testid="response-images-pending">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FFB000] mb-2">
                  attaching with next save ({pendingImages.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {pendingImages.map((p, idx) => (
                    <div key={idx} className="relative w-24 h-16 border border-[#FFB000]/40 bg-black overflow-hidden group">
                      <img src={p.preview} alt={p.filename} className="w-full h-full object-cover" />
                      <button
                        onClick={() => removePending(idx)}
                        data-testid={`remove-pending-${idx}`}
                        className="absolute top-0 right-0 bg-black/80 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="remove"
                      ><X className="w-3 h-3" /></button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-[9px] font-mono text-neutral-400 px-1 py-0.5 truncate">{p.sizeKB} kb</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                data-testid="attach-screenshot-btn"
                disabled={(review.response_images?.length || 0) + pendingImages.length >= MAX_RESPONSE_IMAGES}
                className="inline-flex items-center gap-1.5 border border-neutral-800 hover:border-[#00E5FF] hover:text-[#00E5FF] text-neutral-300 font-mono text-xs uppercase tracking-[0.2em] px-3 py-2 transition-colors disabled:opacity-40"
              >
                <ImagePlus className="w-3.5 h-3.5" /> attach screenshot
              </button>
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600">
                up to {MAX_RESPONSE_IMAGES} · png/jpg/webp · 2 mb each
              </span>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                multiple
                className="hidden"
                data-testid="attach-screenshot-input"
                onChange={(e) => { addImages(e.target.files); e.target.value = ""; }}
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600">
                {review.responded_at ? `last saved ${new Date(review.responded_at).toLocaleString()}` : "draft"}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => saveResponse("in_progress")}
                  disabled={saving}
                  data-testid="save-draft"
                  className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-neutral-800 hover:border-neutral-600 text-neutral-300 hover:text-white transition-colors disabled:opacity-50"
                >Save draft</button>
                <button
                  onClick={() => saveResponse("completed")}
                  disabled={saving}
                  data-testid="save-complete"
                  className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 bg-white text-black hover:bg-neutral-200 transition-colors disabled:opacity-50"
                >{saving ? "Saving…" : "Save & mark completed"}</button>
              </div>
            </div>
          </div>

          {/* Ticket thread */}
          {review.ticket ? (
            <div className="mt-8 border-t border-neutral-900 pt-6" data-testid="admin-ticket-panel">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFB000]">── follow-up ticket ──</div>
                  <div className="text-sm text-white mt-0.5">
                    Status: <span className="font-mono uppercase tracking-[0.2em]"
                                   style={{ color: review.ticket.status === "open" ? "#00E5FF" : review.ticket.status === "pending" ? "#FFB000" : "#888" }}>
                      {review.ticket.status}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  {["open", "pending", "closed"].map((s) => (
                    <button
                      key={s}
                      onClick={() => setTicketStatus(s)}
                      data-testid={`ticket-status-${s}`}
                      className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1.5 border ${
                        review.ticket.status === s
                          ? "border-white text-white"
                          : "border-neutral-800 text-neutral-500 hover:border-neutral-600 hover:text-white"
                      }`}
                    >
                      {s === "closed" ? <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" />{s}</span>
                                       : s === "open" ? <span className="inline-flex items-center gap-1"><Unlock className="w-3 h-3" />{s}</span>
                                       : s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border border-neutral-900 bg-black/40 max-h-[400px] overflow-y-auto px-4 py-3 space-y-3" data-testid="admin-ticket-messages">
                {(review.ticket.messages || []).map((m) => (
                  <div key={m.id} className={`max-w-[85%] ${m.author === "admin" ? "ml-auto" : ""}`}>
                    <div
                      className={`px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line border ${
                        m.author === "admin"
                          ? "bg-[#FFB000]/[0.06] border-[#FFB000]/30 text-white"
                          : "bg-neutral-900 border-neutral-800 text-neutral-100"
                      }`}
                    >
                      {m.body}
                    </div>
                    <div className={`mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600 ${
                      m.author === "admin" ? "text-right" : ""
                    }`}>
                      {m.author === "admin" ? "you (admin)" : m.author_email || "user"} · {new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
                <div ref={ticketEndRef} />
              </div>

              {review.ticket.status !== "closed" ? (
                <div className="mt-3 flex items-end gap-2">
                  <textarea
                    value={ticketDraft}
                    onChange={(e) => setTicketDraft(e.target.value)}
                    rows={2}
                    placeholder="Reply to user…"
                    data-testid="admin-ticket-input"
                    className="flex-1 bg-black border border-neutral-800 px-3 py-2 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-[#FFB000] resize-none"
                  />
                  <button
                    onClick={sendTicketReply}
                    disabled={ticketSending || !ticketDraft.trim()}
                    data-testid="admin-ticket-send"
                    className="inline-flex items-center gap-1.5 bg-white text-black font-bold text-xs uppercase tracking-wider px-4 py-3 hover:bg-neutral-200 disabled:opacity-50 transition-colors"
                  ><Send className="w-3.5 h-3.5" /> Send</button>
                </div>
              ) : (
                <div className="mt-3 text-xs text-neutral-500 inline-flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> Ticket is closed — read-only for the user.
                </div>
              )}
            </div>
          ) : (
            <div className="mt-6 text-xs font-mono uppercase tracking-[0.25em] text-neutral-700">
              no follow-up ticket yet
            </div>
          )}
        </div>
      </motion.div>

      {/* Image lightbox */}
      {enlarged && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setEnlarged(null)}
          data-testid="admin-image-lightbox"
        >
          <div className="absolute top-4 right-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <a
              href={enlarged.src}
              download={enlarged.filename || "chartlenz-screenshot.png"}
              data-testid="lightbox-download"
              className="inline-flex items-center gap-1.5 bg-white text-black font-mono text-xs uppercase tracking-[0.2em] px-3 py-2 hover:bg-neutral-200"
            >
              <Download className="w-3.5 h-3.5" /> download
            </a>
            <button onClick={() => setEnlarged(null)} className="text-white p-2" aria-label="close">
              <X className="w-6 h-6" />
            </button>
          </div>
          <img src={enlarged.src} alt={enlarged.filename || "chart"} className="max-w-full max-h-full object-contain border border-neutral-800" />
        </div>
      )}
    </section>
  );
}

function ResponseImageThumb({ onClick, onDelete, label, testid }) {
  return (
    <div className="relative w-24 h-16 border border-neutral-800 bg-black overflow-hidden group" data-testid={testid}>
      <button
        onClick={onClick}
        title={label}
        className="absolute inset-0 flex items-center justify-center text-[10px] font-mono uppercase tracking-[0.15em] text-neutral-500 hover:text-[#00E5FF] hover:bg-[#00E5FF]/[0.04] transition-colors"
      >
        <Maximize2 className="w-4 h-4" />
      </button>
      <button
        onClick={onDelete}
        className="absolute top-0 right-0 bg-black/80 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="delete"
      >
        <Trash2 className="w-3 h-3 text-red-400" />
      </button>
    </div>
  );
}
